import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PreferencesProvider } from "./context/PreferencesContext";
import { SiteProvider } from "./context/SiteContext";
import Login from "./components/Login";
import ResetPassword from "./components/ResetPassword";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import RouteFallback from "./components/RouteFallback";
import { Toaster } from "./components/ui/toaster";
import {
  DIRECTION_ROLES,
  DIRECTION_MENU_ROLES,
  ALL_OPERATIONAL,
  ALL_OPERATIONAL_WITH_ADMIN,
  TONNAGE_AUTHORIZATION_ROLES,
  DATACENTER_DASHBOARD_ROLES,
  BUILDING_DASHBOARD_ROLES,
  EXPORT_ROLES,
  PRODUCTION_DASHBOARD_ROLES,
  PRODUCTION_METRICS_ROLES,
  MAINTENANCE_DASHBOARD_ROLES,
} from "./utils/roles";

// ──────────────────────────────────────────────────────────────────────
// Lazy loading par route — chaque dashboard = chunk JS séparé chargé à
// la demande. Bénéfices :
//   - Bundle initial plus léger (login + framework, pas tous les graphes)
//   - Un crash de chargement (chunk corrompu, fichier manquant) reste
//     isolé à la route concernée grâce à <ErrorBoundary>.
// ──────────────────────────────────────────────────────────────────────

// User dashboards
const DashboardProduction = lazy(() => import("./pages/DashboardProduction"));
const DashboardChefProduction = lazy(() => import("./pages/DashboardChefProduction"));
const DashboardMaintenance = lazy(() => import("./pages/DashboardMaintenance"));
const DashboardDirection = lazy(() => import("./pages/DashboardDirection"));
const DashboardZones = lazy(() => import("./pages/DashboardZones"));
const DashboardComparison = lazy(() => import("./pages/DashboardComparison"));
const DashboardBreakers = lazy(() => import("./pages/DashboardBreakers"));
const DashboardAlerts = lazy(() => import("./pages/DashboardAlerts"));
const DashboardMultiSite = lazy(() => import("./pages/DashboardMultiSite"));
const DashboardProductionMetrics = lazy(() => import("./pages/DashboardProductionMetrics"));
const DashboardExport = lazy(() => import("./pages/DashboardExport"));
const DashboardFacility = lazy(() => import("./pages/DashboardFacility"));     // datacenter : PUE détaillé
const DashboardEnergyDensity = lazy(() => import("./pages/DashboardEnergyDensity")); // office : kWh/m² détaillé
// Dashboards par rôle dédié (datacenter / office)
const DashboardDcExploitation = lazy(() => import("./pages/DashboardDcExploitation"));   // datacenter : ops 24/7
const DashboardDcFacility = lazy(() => import("./pages/DashboardDcFacility"));           // datacenter : génie clim/élec
const DashboardDcRedundancy = lazy(() => import("./pages/DashboardDcRedundancy"));       // datacenter : redondance 2N (voies A/B)
const DashboardBuildingManager = lazy(() => import("./pages/DashboardBuildingManager")); // office : gestion FM
const DashboardBuildingTechnician = lazy(() => import("./pages/DashboardBuildingTechnician")); // office : maintenance technique
// water_production — Phase 1 SODECI : monitoring pompes/moteurs
const DashboardPumpingStation = lazy(() => import("./pages/DashboardPumpingStation"));
// Écran TV d'usine — rotation plein écran des dashboards whitelist (rôle "affichage")
const DashboardTv = lazy(() => import("./pages/DashboardTv"));

// Admin pages (super_admin)
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminClients = lazy(() => import("./pages/admin/AdminClients"));
const AdminLicenses = lazy(() => import("./pages/admin/AdminLicenses"));
const AdminLicenseDetails = lazy(() => import("./pages/admin/AdminLicenseDetails"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAlerts = lazy(() => import("./pages/admin/AdminAlerts"));
const AdminStatistics = lazy(() => import("./pages/admin/AdminStatistics"));
const AdminAnalyse = lazy(() => import("./pages/admin/AdminAnalyse"));
const AdminClientDetails = lazy(() => import("./pages/admin/AdminClientDetails"));
const AdminPKI = lazy(() => import("./pages/admin/AdminPKI"));
const AdminDatabaseCleanup = lazy(() => import("./pages/admin/AdminDatabaseCleanup"));

// Client Admin pages
const ClientAdminLayout = lazy(() => import("./components/client-admin/ClientAdminLayout"));
const ClientAdminDashboard = lazy(() => import("./pages/client-admin/ClientAdminDashboard"));
const ClientAdminUsers = lazy(() => import("./pages/client-admin/ClientAdminUsers"));
const ClientAdminLicense = lazy(() => import("./pages/client-admin/ClientAdminLicense"));
const ClientAdminAlerts = lazy(() => import("./pages/client-admin/ClientAdminAlerts"));
const ClientAdminSupport = lazy(() => import("./pages/client-admin/ClientAdminSupport"));
const ClientAdminSettings = lazy(() => import("./pages/client-admin/ClientAdminSettings"));

// ──────────────────────────────────────────────────────────────────────
// Helper : combine ErrorBoundary + Suspense pour chaque route lazy-loaded.
// Un crash runtime ou un échec de chargement n'impacte pas les autres routes.
// ──────────────────────────────────────────────────────────────────────
const SafeRoute = ({ name, children }) => (
  <ErrorBoundary routeName={name}>
    <Suspense fallback={<RouteFallback />}>{children}</Suspense>
  </ErrorBoundary>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

const RoleRoute = ({ children, allowedRoles, industryKey }) => {
  const { user } = useAuth();

  if (!user || !allowedRoles.includes(user.role)) {
    // Rediriger vers le dashboard approprié selon le rôle + whitelist
    // (source unique : getLandingDashboard — inclut 'affichage' →
    // /dashboard/tv, l'écran TV ne peut donc pas atterrir sur une route
    // métier classique).
    return <Navigate to={getLandingDashboard(user)} replace />;
  }

  // Enforcement whitelist par-user : si une whitelist est active
  // (user.enabled_dashboards est un tableau, rôle non exempté) et que le
  // dashboard de cette route n'y figure pas, l'accès direct par URL est
  // bloqué. Les routes sans industryKey (export, admin…) ne sont pas gatées.
  if (industryKey) {
    const whitelist = getUserDashboardWhitelist(user);
    if (whitelist && !whitelist.includes(industryKey)) {
      const firstAllowed = getFirstAllowedDashboardPath(user);
      if (firstAllowed) return <Navigate to={firstAllowed} replace />;
      return <NoDashboardAllowed />;
    }
  }

  return children;
};

// Route protégée pour les super admins uniquement
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'super_admin') {
    return <Navigate to={getLandingDashboard(user)} replace />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <AdminLayout>{children}</AdminLayout>
    </Suspense>
  );
};

// Route pour le dashboard Analyse (super_admin + analyste)
const AnalyseRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'super_admin' && user.role !== 'analyste') {
    return <Navigate to={getLandingDashboard(user)} replace />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <AdminLayout>{children}</AdminLayout>
    </Suspense>
  );
};

// Route protégée pour les admins client (rôle "admin" uniquement)
const ClientAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Seuls les utilisateurs avec rôle "admin" ou "admin_client" peuvent accéder à l'admin client
  if (user.role !== 'admin' && user.role !== 'admin_client') {
    return <Navigate to={getLandingDashboard(user)} replace />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <ClientAdminLayout>{children}</ClientAdminLayout>
    </Suspense>
  );
};

// Route plein écran pour l'écran TV d'usine — PAS de Layout (aucune
// sidebar / aucun header) : le contenu occupe tout l'écran mural.
// Accès : 'affichage' (compte écran TV, lecture seule) + super_admin /
// admin_client (prévisualisation de l'écran par les admins).
// NB : rôles écrits en dur (pas d'import utils/roles.js) car ce fichier
// est modifié en parallèle par un autre chantier.
const TvRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!['affichage', 'super_admin', 'admin_client'].includes(user.role)) {
    return <Navigate to={getLandingDashboard(user)} replace />;
  }

  return children;
};

// Fonction pour obtenir le dashboard par défaut selon le rôle
const getDefaultDashboard = (role) => {
  switch (role) {
    case 'super_admin':
      return '/admin/dashboard';
    case 'admin':
    case 'admin_client':
      return '/client-admin/dashboard';  // Admin Client uniquement
    case 'direction':
    case 'direction_centrale':
    case 'energy_manager':
      return '/dashboard/direction';     // Direction / responsable énergie → coûts & KPIs
    case 'maintenance':
      return '/dashboard/maintenance';
    case 'analyste':
      return '/admin/analyse';
    // Écran TV d'usine (lecture seule) — mode plein écran en rotation
    case 'affichage':
      return '/dashboard/tv';
    // Datacenter
    case 'dc_exploitation':
      return '/dashboard/dc-exploitation';
    case 'dc_facility':
      return '/dashboard/dc-facility';
    // Office / Bureaux
    case 'building_manager':
      return '/dashboard/building-manager';
    case 'building_technician':
      return '/dashboard/building-technician';
    case 'production':
    default:
      return '/dashboard/production';
  }
};

// ──────────────────────────────────────────────────────────────────────
// Whitelist par-utilisateur (user.enabled_dashboards) — enforcement au
// niveau des ROUTES (la sidebar est déjà filtrée dans Layout.jsx).
// Sémantique identique à Layout.jsx : null / undefined = pas de
// restriction (comportement legacy), tableau (même vide) = seuls les
// industryKeys listés sont accessibles.
// Jamais gatés : super_admin / admin / admin_client (rôles admin),
// affichage (la whitelist lui sert de liste de rotation sur /dashboard/tv),
// /dashboard/tv, /dashboard/export (permissions d'export), /admin/*,
// /client-admin/*.
// ──────────────────────────────────────────────────────────────────────
const WHITELIST_EXEMPT_ROLES = ['super_admin', 'admin', 'admin_client', 'affichage'];

// industryKey → rôles autorisés — copie EXACTE des allowedRoles des routes
// /dashboard/* ci-dessous (même correspondance path → industryKey que le
// menu de Layout.jsx). Le path d'un dashboard est toujours
// `/dashboard/${industryKey}`.
const DASHBOARD_ROUTE_ROLES = {
  'production': PRODUCTION_DASHBOARD_ROLES,
  'chef-production': TONNAGE_AUTHORIZATION_ROLES,
  'maintenance': MAINTENANCE_DASHBOARD_ROLES,
  'dc-exploitation': DATACENTER_DASHBOARD_ROLES,
  'dc-facility': DATACENTER_DASHBOARD_ROLES,
  'dc-redundancy': DATACENTER_DASHBOARD_ROLES,
  'building-manager': BUILDING_DASHBOARD_ROLES,
  'building-technician': BUILDING_DASHBOARD_ROLES,
  'pumping-station': PRODUCTION_DASHBOARD_ROLES,
  'direction': DIRECTION_MENU_ROLES,
  'zones': ALL_OPERATIONAL,
  'comparison': ALL_OPERATIONAL,
  'breakers': ALL_OPERATIONAL,
  'alerts': ALL_OPERATIONAL,
  'production-metrics': PRODUCTION_METRICS_ROLES,
  'multi-site': DIRECTION_ROLES,
  'facility': ALL_OPERATIONAL_WITH_ADMIN,
  'energy-density': ALL_OPERATIONAL_WITH_ADMIN,
};

// Whitelist effective d'un user : null = pas de restriction (champ absent,
// null, ou rôle exempté), tableau (même vide) = restriction active.
const getUserDashboardWhitelist = (user) => {
  if (!user || WHITELIST_EXEMPT_ROLES.includes(user.role)) return null;
  return Array.isArray(user.enabled_dashboards) ? user.enabled_dashboards : null;
};

// Premier dashboard de la whitelist accessible au rôle du user (évite de
// rediriger vers une route que RoleRoute refuserait → boucle de redirects).
// null si whitelist inactive, vide, ou sans dashboard compatible.
const getFirstAllowedDashboardPath = (user) => {
  const whitelist = getUserDashboardWhitelist(user);
  if (!whitelist) return null;
  const key = whitelist.find((k) => {
    const roles = DASHBOARD_ROUTE_ROLES[k];
    return Array.isArray(roles) && roles.includes(user.role);
  });
  return key ? `/dashboard/${key}` : null;
};

// Destination d'atterrissage d'un user (login, route /, redirects de rôle) :
// le défaut du rôle, SAUF si une whitelist est active et ne le contient pas
// — dans ce cas le premier dashboard autorisé de la whitelist. Si aucun
// (liste vide / incompatible avec le rôle), on garde le défaut du rôle :
// sa route affichera la mini-page « aucun dashboard autorisé » (RoleRoute).
const getLandingDashboard = (user) => {
  const fallback = getDefaultDashboard(user?.role);
  const whitelist = getUserDashboardWhitelist(user);
  if (!whitelist) return fallback;
  const match = fallback.match(/^\/dashboard\/(.+)$/);
  const defaultKey = match ? match[1] : null;
  // Défaut hors périmètre whitelist (/admin/*, /client-admin/*, tv…) → inchangé.
  if (!defaultKey || !Object.prototype.hasOwnProperty.call(DASHBOARD_ROUTE_ROLES, defaultKey)) {
    return fallback;
  }
  if (whitelist.includes(defaultKey)) return fallback;
  return getFirstAllowedDashboardPath(user) || fallback;
};

// Mini-page affichée quand la whitelist du user est vide (ou ne contient
// aucun dashboard compatible avec son rôle). Rendue dans le Layout — la
// sidebar, filtrée par la même whitelist, est alors vide elle aussi.
const NoDashboardAllowed = () => (
  <div className="min-h-[60vh] flex items-center justify-center px-6">
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 max-w-md text-center">
      <h2 className="text-white text-lg font-semibold mb-2">Aucun dashboard autorisé</h2>
      <p className="text-slate-400 text-sm">
        Aucun dashboard n'est autorisé pour votre compte : contactez votre administrateur.
      </p>
    </div>
  </div>
);

function AppContent() {
  const { user } = useAuth();

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to={getLandingDashboard(user)} replace /> : <Login />}
        />
        {/* Page publique : accessible sans connexion, le jeton du lien fait foi.
            Reste atteignable meme connecte, pour qu'un lien ouvert dans un
            navigateur deja authentifie fonctionne quand meme. */}
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/dashboard/production"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={PRODUCTION_DASHBOARD_ROLES} industryKey="production">
                <SafeRoute name="Production"><DashboardProduction /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/chef-production"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={TONNAGE_AUTHORIZATION_ROLES} industryKey="chef-production">
                <SafeRoute name="Validations Tonnage"><DashboardChefProduction /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/maintenance"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={MAINTENANCE_DASHBOARD_ROLES} industryKey="maintenance">
                <SafeRoute name="Maintenance"><DashboardMaintenance /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/direction"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={DIRECTION_MENU_ROLES} industryKey="direction">
                <SafeRoute name="Direction"><DashboardDirection /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/zones"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={ALL_OPERATIONAL} industryKey="zones">
                <SafeRoute name="Zones"><DashboardZones /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/comparison"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={ALL_OPERATIONAL} industryKey="comparison">
                <SafeRoute name="Comparaison"><DashboardComparison /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/breakers"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={ALL_OPERATIONAL} industryKey="breakers">
                <SafeRoute name="Disjoncteurs"><DashboardBreakers /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/alerts"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={ALL_OPERATIONAL} industryKey="alerts">
                <SafeRoute name="Alertes"><DashboardAlerts /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/multi-site"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={DIRECTION_ROLES} industryKey="multi-site">
                <SafeRoute name="Multi-Sites"><DashboardMultiSite /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/production-metrics"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={PRODUCTION_METRICS_ROLES} industryKey="production-metrics">
                <SafeRoute name="Production Metrics"><DashboardProductionMetrics /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Export Route */}
        <Route
          path="/dashboard/export"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={EXPORT_ROLES}>
                <SafeRoute name="Export"><DashboardExport /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Multi-industry — datacenter PUE */}
        <Route
          path="/dashboard/facility"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={ALL_OPERATIONAL_WITH_ADMIN} industryKey="facility">
                <SafeRoute name="Facility (PUE)"><DashboardFacility /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Multi-industry — office kWh/m² */}
        <Route
          path="/dashboard/energy-density"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={ALL_OPERATIONAL_WITH_ADMIN} industryKey="energy-density">
                <SafeRoute name="Energy Density"><DashboardEnergyDensity /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Datacenter — Exploitation 24/7 (NOC / DCIM) */}
        <Route
          path="/dashboard/dc-exploitation"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={DATACENTER_DASHBOARD_ROLES} industryKey="dc-exploitation">
                <SafeRoute name="DC Exploitation"><DashboardDcExploitation /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Datacenter — Facility (génie clim & élec) */}
        <Route
          path="/dashboard/dc-facility"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={DATACENTER_DASHBOARD_ROLES} industryKey="dc-facility">
                <SafeRoute name="DC Facility"><DashboardDcFacility /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Datacenter — Redondance 2N (voies A/B en miroir) */}
        <Route
          path="/dashboard/dc-redundancy"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={DATACENTER_DASHBOARD_ROLES} industryKey="dc-redundancy">
                <SafeRoute name="DC Redondance 2N"><DashboardDcRedundancy /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Office — Gestionnaire bâtiment (FM) */}
        <Route
          path="/dashboard/building-manager"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={BUILDING_DASHBOARD_ROLES} industryKey="building-manager">
                <SafeRoute name="Building Manager"><DashboardBuildingManager /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Office — Maintenance technique */}
        <Route
          path="/dashboard/building-technician"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={BUILDING_DASHBOARD_ROLES} industryKey="building-technician">
                <SafeRoute name="Building Technician"><DashboardBuildingTechnician /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* water_production — Phase 1 SODECI : monitoring pompes/moteurs */}
        <Route
          path="/dashboard/pumping-station"
          element={
            <ProtectedRoute>
              <RoleRoute allowedRoles={PRODUCTION_DASHBOARD_ROLES} industryKey="pumping-station">
                <SafeRoute name="Pumping Station"><DashboardPumpingStation /></SafeRoute>
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        {/* Écran TV d'usine — plein écran, HORS Layout (rotation des
            dashboards de user.enabled_dashboards, ?interval=NN en secondes) */}
        <Route
          path="/dashboard/tv"
          element={
            <TvRoute>
              <SafeRoute name="Écran TV"><DashboardTv /></SafeRoute>
            </TvRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/dashboard"
          element={
            <AdminRoute>
              <SafeRoute name="Admin Dashboard"><AdminDashboard /></SafeRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/clients"
          element={
            <AdminRoute>
              <SafeRoute name="Admin Clients"><AdminClients /></SafeRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/clients/:clientId"
          element={
            <AdminRoute>
              <SafeRoute name="Admin Client Details"><AdminClientDetails /></SafeRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/licenses"
          element={
            <AdminRoute>
              <SafeRoute name="Admin Licenses"><AdminLicenses /></SafeRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/licenses/:licenseId"
          element={
            <AdminRoute>
              <SafeRoute name="Admin License Details"><AdminLicenseDetails /></SafeRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <SafeRoute name="Admin Users"><AdminUsers /></SafeRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/pki"
          element={
            <AdminRoute>
              <SafeRoute name="Admin PKI"><AdminPKI /></SafeRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/database"
          element={
            <AdminRoute>
              <SafeRoute name="Admin Database Cleanup"><AdminDatabaseCleanup /></SafeRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/alerts"
          element={
            <AdminRoute>
              <SafeRoute name="Admin Alerts"><AdminAlerts /></SafeRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/statistics"
          element={
            <AdminRoute>
              <SafeRoute name="Admin Statistics"><AdminStatistics /></SafeRoute>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/analyse"
          element={
            <AnalyseRoute>
              <SafeRoute name="Analyse Sites"><AdminAnalyse /></SafeRoute>
            </AnalyseRoute>
          }
        />
        <Route
          path="/admin/export"
          element={
            <AdminRoute>
              <SafeRoute name="Admin Export"><DashboardExport /></SafeRoute>
            </AdminRoute>
          }
        />

        {/* Client Admin Routes */}
        <Route
          path="/client-admin/dashboard"
          element={
            <ClientAdminRoute>
              <SafeRoute name="Client Admin Dashboard"><ClientAdminDashboard /></SafeRoute>
            </ClientAdminRoute>
          }
        />
        <Route
          path="/client-admin/users"
          element={
            <ClientAdminRoute>
              <SafeRoute name="Client Admin Users"><ClientAdminUsers /></SafeRoute>
            </ClientAdminRoute>
          }
        />
        <Route
          path="/client-admin/license"
          element={
            <ClientAdminRoute>
              <SafeRoute name="Client Admin License"><ClientAdminLicense /></SafeRoute>
            </ClientAdminRoute>
          }
        />
        <Route
          path="/client-admin/alerts"
          element={
            <ClientAdminRoute>
              <SafeRoute name="Client Admin Alerts"><ClientAdminAlerts /></SafeRoute>
            </ClientAdminRoute>
          }
        />
        <Route
          path="/client-admin/support"
          element={
            <ClientAdminRoute>
              <SafeRoute name="Client Admin Support"><ClientAdminSupport /></SafeRoute>
            </ClientAdminRoute>
          }
        />
        <Route
          path="/client-admin/settings"
          element={
            <ClientAdminRoute>
              <SafeRoute name="Client Admin Settings"><ClientAdminSettings /></SafeRoute>
            </ClientAdminRoute>
          }
        />

        <Route path="/" element={user ? <Navigate to={getLandingDashboard(user)} replace /> : <Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      <Toaster />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <PreferencesProvider>
        <SiteProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </SiteProvider>
      </PreferencesProvider>
    </AuthProvider>
  );
}

export default App;
